import React, { useEffect, useMemo, useState } from "react";
import { PostLogin } from "../component/Header";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import Helmet from "react-helmet";
import SettingSidebar from "../component/Settings/SettingSidebar";
import SettingAccount from "./Settings/Profile/SettingAccount";
import SettingWhatsappQrCode from "./Settings/WhatsAppQrCode/SettingWhatsappQrCode";
import SettingWebsiteManager from "../component/Settings/SettingWebsiteManager";
import SettingPlanSubscription from "./SettingPlanSubscription";
import SettingPartnership from "./Settings/SettingPartnership";
import { Dimmer, Loader } from "semantic-ui-react";

import useRouteConfig from "../config/useRouteConfig";
import SettingLiveChatWidget from "../component/Settings/SettingLiveChatWidget";
import { useTranslation } from "react-i18next";
import SettingTemplateMessage from "./Settings/OfficialWhatsApp/SettingTemplateMessage";
import SettingWhatsappBilling from "./Settings/OfficialWhatsApp/SettingBilling";
import SettingOptIn from "./Settings/OfficialWhatsApp/SettingOptIn";
import InboxManagement from "../component/Settings/SettingInbox/Inbox/InboxManagement";
import { useAppSelector } from "../AppRootContext";
import { isAdminRole } from "../component/Settings/helpers/AccessRulesGuard";
import BackupWhatsappChats from "./Settings/BackupWhatsappChats";
import { LabelsManagement } from "../component/Settings/SettingInbox/Labels/LabelsManagement";
import SettingPaymentLink from "features/Ecommerce/Payment/usecases/Settings/SettingPaymentLink";
import SettingSalesforce from "features/Salesforce/usecases/Settings/SettingSalesforce";
import SettingHubspot from "features/Hubspot/usecases/Settings/SettingHubspot";
import { useAccessRulesGuard } from "../component/Settings/hooks/useAccessRulesGuard";
import { equals } from "ramda";
import { CommerceSettings } from "../features/Ecommerce/usecases/Settings/Commerce/CommerceSettings";
import { CompanyPoliciesProvider } from "component/Settings/SettingInbox/Inbox/CompanyPoliciesContext";
import SettingDeprecatedV2 from "component/Settings/SettingDeprecatedV2";
import useDeprecateFeatureMapping from "hook/useDeprecateFeatureMapping";
import { PERMISSION_KEY } from "types/Rbac/permission";
import { usePermission } from "component/shared/usePermission";
import { useFeaturesGuard } from "component/Settings/hooks/useFeaturesGuard";

const Settings = ({ match }: RouteComponentProps<any>) => {
  const history = useHistory();
  const { path, staffId, subPath } = match.params;
  const { pathname } = useLocation();
  const { loggedInUserDetail } = useAppSelector(
    (s) => ({
      loggedInUserDetail: s.loggedInUserDetail,
    }),
    equals
  );
  const accessRuleGuard = useAccessRulesGuard();

  const isResellerClient = accessRuleGuard.isResellerClient();
  const { routeTo } = useRouteConfig();
  const { t } = useTranslation();
  const isAdmin = loggedInUserDetail && isAdminRole(loggedInUserDetail);
  let initMenuItem: string;
  switch (true) {
    case pathname.startsWith(routeTo("/settings/whatsappQrCode")):
      initMenuItem = "whatsappQrCode";
      break;
    case typeof subPath === "string":
      initMenuItem = `${subPath}`;
      break;
    case typeof path === "string":
      initMenuItem = path;
      break;
    case typeof staffId === "string":
      initMenuItem = "usermanagement";
      break;
    case pathname.startsWith(routeTo("/settings/teams")):
      initMenuItem = "teams";
      break;
    case pathname.startsWith(routeTo("/settings/templates")):
      initMenuItem = "templates";
      break;
    default:
      initMenuItem = "generalinfo";
      break;
  }

  useEffect(() => {
    if (!path && initMenuItem === "generalinfo") {
      history.push(routeTo("/settings/generalinfo"));
    }
  }, [path, initMenuItem, history, routeTo]);

  const [menuItem, updateMenuItem] = useState(initMenuItem);
  const [loading, isLoading] = useState(false);

  useEffect(() => {
    updateMenuItem(initMenuItem);
  }, [initMenuItem]);

  const isRbacEnabled = useAppSelector((s) => s.isRbacEnabled);
  const canAccessSettings = isRbacEnabled
    ? true
    : accessRuleGuard.canAccessSettings();

  const permission = usePermission();
  const featureGuard = useFeaturesGuard();
  const isRequested360dialog = useAppSelector((s) =>
    s.company?.whatsApp360DialogUsageRecords
      ? s.company.whatsApp360DialogUsageRecords.length > 0
      : false
  );
  const isRequestedTwilio = useAppSelector((s) =>
    s.company?.twilioUsageRecords
      ? s.company.twilioUsageRecords.length > 0
      : false
  );
  const ableToUseOfficial = accessRuleGuard.canUseOfficialWhatsapp();
  const ableToTopUp =
    (isRequestedTwilio && accessRuleGuard.isTwilioSubaccount()) ||
    accessRuleGuard.isCloudAPIUsageRecordExist() ||
    (isRequested360dialog && accessRuleGuard.is360DialogAccount());

  const permissions = useMemo(() => {
    return permission.check(
      [
        PERMISSION_KEY.plansAndBillingsPlanAndSubscriptionManage,
        PERMISSION_KEY.companySettingsUserView,
        PERMISSION_KEY.companySettingsTeamView,
        PERMISSION_KEY.channelQRCodeManage,
        PERMISSION_KEY.inboxSettingsManage,
        PERMISSION_KEY.inboxSavedRepliesView,
        PERMISSION_KEY.channelView,
        PERMISSION_KEY.channelTemplateView,
        PERMISSION_KEY.channelOptInManage,
        PERMISSION_KEY.channelBillingManage,
        PERMISSION_KEY.channelEdit,
      ],
      [
        (accessRuleGuard.canAccessPlanAndSubscription() ||
          featureGuard.isShopifyAccount()) &&
          featureGuard.canShowPlanSubscription() &&
          !isResellerClient,
        canAccessSettings,
        canAccessSettings,
        !!isAdmin && !isResellerClient,
        canAccessSettings,
        true,
        true,
        true,
        true,
        featureGuard.canShowTopUp(),
        canAccessSettings,
      ]
    );
  }, [permission.check, isResellerClient, canAccessSettings, isAdmin]);

  const [
    canManagePlansAndBillings,
    canViewUserManagement,
    canViewTeamManagement,
    canManageWhatsappQrCode,
    canManageInboxSettings,
    canViewSavedReplies,
    canViewChannels,
    canViewOfficialWhatsAppTemplates,
    canManageOptIn,
    canManageChannelBilling,
    canEditChannels,
  ] = permissions;

  const deprecateFeatureMapping = useDeprecateFeatureMapping();

  const pageTitle = t("nav.menu.settings.settings");
  return loading ? (
    <Dimmer inverted active>
      <Loader />
    </Dimmer>
  ) : (
    <div className="post-login">
      <PostLogin selectedItem={""}>
        <Helmet title={t("nav.common.title", { page: pageTitle })} />
        <div className="settings main">
          <SettingSidebar menuItem={menuItem} updateMenuItem={updateMenuItem} />
          {menuItem === "generalinfo" && <SettingAccount />}
          {menuItem === "quickreplies" && canViewSavedReplies && (
            <SettingDeprecatedV2
              moduleName={deprecateFeatureMapping.savedReplies.moduleName}
              redirectUrl={deprecateFeatureMapping.savedReplies.redirectUrl}
              helpCenterUrl={deprecateFeatureMapping.savedReplies.helpCenterUrl}
            />
          )}
          {menuItem === "teams" && canViewTeamManagement && (
            <SettingDeprecatedV2
              moduleName={deprecateFeatureMapping.teamManagement.moduleName}
              redirectUrl={deprecateFeatureMapping.teamManagement.redirectUrl}
              helpCenterUrl={
                deprecateFeatureMapping.teamManagement.helpCenterUrl
              }
            />
          )}
          {menuItem === "inbox" && canManageInboxSettings && (
            <CompanyPoliciesProvider>
              <InboxManagement />
            </CompanyPoliciesProvider>
          )}
          {menuItem === "whatsappQrCode" && canManageWhatsappQrCode && (
            <SettingWhatsappQrCode />
          )}
          {menuItem === "whatsappBackupChats" && canEditChannels && (
            <SettingDeprecatedV2
              moduleName={
                deprecateFeatureMapping.whatsappBackupChats.moduleName
              }
              redirectUrl={
                deprecateFeatureMapping.whatsappBackupChats.redirectUrl
              }
              helpCenterUrl={
                deprecateFeatureMapping.whatsappBackupChats.helpCenterUrl
              }
            />
          )}
          {menuItem === "websitemessenger" && (
            <SettingWebsiteManager loadingCompany={loading} />
          )}
          {menuItem.includes("templates") &&
            ableToUseOfficial &&
            canViewOfficialWhatsAppTemplates && <SettingTemplateMessage />}
          {menuItem === "topup" && ableToTopUp && canManageChannelBilling && (
            <SettingWhatsappBilling />
          )}
          {menuItem === "opt-in" &&
            ableToUseOfficial &&
            accessRuleGuard.canShowOptIn() &&
            canManageOptIn && <SettingOptIn />}
          {menuItem === "labels" && canManageInboxSettings && (
            <SettingDeprecatedV2
              moduleName={deprecateFeatureMapping.labelsManagement.moduleName}
              redirectUrl={deprecateFeatureMapping.labelsManagement.redirectUrl}
              helpCenterUrl={
                deprecateFeatureMapping.labelsManagement.helpCenterUrl
              }
            />
          )}
          {menuItem.includes("usermanagement") && canViewUserManagement && (
            <SettingDeprecatedV2
              moduleName={deprecateFeatureMapping.userManagement.moduleName}
              redirectUrl={deprecateFeatureMapping.userManagement.redirectUrl}
              helpCenterUrl={
                deprecateFeatureMapping.userManagement.helpCenterUrl
              }
            />
          )}
          {menuItem === "plansubscription" &&
            canManagePlansAndBillings &&
            !isResellerClient && <SettingPlanSubscription />}
          {menuItem === "partnership" && isAdmin && <SettingPartnership />}
          {menuItem === "livechatwidget" && canViewChannels && (
            <SettingDeprecatedV2
              moduleName={deprecateFeatureMapping.liveChatWidget.moduleName}
              redirectUrl={deprecateFeatureMapping.liveChatWidget.redirectUrl}
              helpCenterUrl={
                deprecateFeatureMapping.liveChatWidget.helpCenterUrl
              }
            />
          )}
          {menuItem === "paymentlink" &&
            featureGuard.canUseStripePayments() && <SettingPaymentLink />}
          {menuItem === "salesforce" &&
            featureGuard.canUseSalesforceCrm() &&
            isAdmin && <SettingSalesforce />}
          {menuItem === "hubspot" &&
            featureGuard.canUseHubspotCrm() &&
            isAdmin && (
              <SettingDeprecatedV2
                moduleName={deprecateFeatureMapping.hubspot.moduleName}
                redirectUrl={deprecateFeatureMapping.hubspot.redirectUrl}
                helpCenterUrl={deprecateFeatureMapping.hubspot.helpCenterUrl}
              />
            )}
          {menuItem === "commerce" && isAdmin && <CommerceSettings />}
        </div>
      </PostLogin>
    </div>
  );
};

export default Settings;
